/**
 * Human-readable storage sizes from a megabyte count.
 *
 * Plan quotas come off the API in MB (`plans.max_storage_mb`), which reads
 * badly once the numbers get large — "1024 MB" and "51200 MB" are the plan's
 * 1 GB and 50 GB, but the user has to do the division. Anything at or above a
 * gigabyte is rendered in GB.
 *
 * Deliberately NOT built on `formatFileSize` (utils/fileUtils.ts): that one
 * takes BYTES and starts its unit ladder at "Bytes", so a plan quota would have
 * to be multiplied up only to be divided back down, and a sub-MB rounding error
 * could render a quota as "0.99 MB".
 */

/** Megabytes per gigabyte — binary, matching how the quotas are defined. */
const MB_PER_GB = 1024;

/** Drop a trailing `.0` so whole sizes read "15 GB", not "15.0 GB". */
function trimTrailingZero(value: number, fractionDigits: number): string {
  return Number.parseFloat(value.toFixed(fractionDigits)).toString();
}

/**
 * Format a megabyte count for display, e.g. `1024 -> "1 GB"`, `512 -> "512 MB"`.
 *
 * Values at or above 1 GB render in GB with at most one decimal (so 1536 MB is
 * "1.5 GB"); below that they stay in MB. Negative input is treated as 0 — a
 * negative quota is not meaningful and should not surface as "-1 GB".
 */
export function formatStorageSizeMb(megabytes: number): string {
  if (!Number.isFinite(megabytes) || megabytes <= 0) return '0 MB';
  if (megabytes >= MB_PER_GB) {
    return `${trimTrailingZero(megabytes / MB_PER_GB, 1)} GB`;
  }
  return `${trimTrailingZero(megabytes, 1)} MB`;
}
